/**
 * Add business days to a date, skipping weekends
 * @param {Date} startDate - Starting date
 * @param {number} businessDays - Number of business days to add
 * @returns {Date} - Resulting date after adding business days
 */
export const addBusinessDays = (startDate, businessDays) => {
  const result = new Date(startDate);
  let daysAdded = 0;

  while (daysAdded < businessDays) {
    result.setDate(result.getDate() + 1);
    const dayOfWeek = result.getDay();
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      daysAdded++;
    }
  }

  return result;
};

/**
 * Calculate dayType or creativeDayType based on job type and duration
 * @param {string} jobTypeFull - Job type (e.g., "Life (-L)", "Creative Product (-PC)")
 * @param {string|Date|Object} bookedStart - Start time
 * @param {string|Date|Object} bookedFinish - End time
 * @returns {Object|null} - {dayType, creativeDayType} or null if not applicable
 */
export const calculateDayType = (jobTypeFull, bookedStart, bookedFinish) => {
  if (!jobTypeFull || !bookedStart || !bookedFinish) return null;

  const isCreativeContent = jobTypeFull.includes("-LC") || jobTypeFull.includes("-PC");
  const isLife = jobTypeFull.includes("-L") && !jobTypeFull.includes("-LC");

  if (!isCreativeContent && !isLife) return null;

  const start = bookedStart?.toDate ? bookedStart.toDate() : new Date(bookedStart);
  const finish = bookedFinish?.toDate ? bookedFinish.toDate() : new Date(bookedFinish);
  const durationHours = (finish - start) / (1000 * 60 * 60);
  const dayLength = durationHours <= 4 ? "Half Day" : "Full Day";

  return {
    isCreativeContent,
    dayLength,
    dayType: isLife ? dayLength : "",
    creativeDayType: isCreativeContent ? dayLength : "",
  };
};

/**
 * Get dayType update fields for Firestore
 * @param {string} jobTypeFull - Job type
 * @param {string|Date|Object} bookedStart - Start time
 * @param {string|Date|Object} bookedFinish - End time
 * @returns {Object} - {dayType, creativeDayType} for Firestore
 */
export const getDayTypeUpdateFields = (jobTypeFull, bookedStart, bookedFinish) => {
  const result = calculateDayType(jobTypeFull, bookedStart, bookedFinish);

  if (!result) {
    return { dayType: "", creativeDayType: "" };
  }

  return {
    dayType: result.dayType || "",
    creativeDayType: result.creativeDayType || "",
  };
};
